import { Alert, Badge, Group, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface AuditEntry {
  _id: string;
  seq: number;
  action: string;
  actorUserId: string | null;
  actorRoleKey: string | null;
  target: { kind: string; id: string } | null;
  hash: string;
  prevHash: string | null;
  at: string;
}

interface VerifyResult {
  ok: boolean;
  checked: number;
  brokenAtSeq: number | null;
  reason: string;
}

/**
 * Журнал аудита и состояние его цепочки.
 *
 * Каждая запись содержит хеш предыдущей. Проверка запускается на сервере и
 * показывается здесь, потому что «журнал цел» — утверждение, которое должно
 * быть проверяемым в любой момент, а не только при выгрузке пакета.
 */
export function Audit(): React.JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['audit'],
    queryFn: () => api<{ entries: AuditEntry[] }>('/audit?limit=200'),
  });
  const { data: verify } = useQuery({
    queryKey: ['audit', 'verify'],
    queryFn: () => api<VerifyResult>('/audit/verify'),
    refetchInterval: 60_000,
  });

  const entries = data?.entries ?? [];

  return (
    <Stack gap={0}>
      <PageHeader
        title="Audit log"
        count={isLoading ? undefined : entries.length}
        hint="Append-only and hash-chained: every entry carries the hash of the one before it, so an edit after the fact leaves a visible break."
      />

      {verify !== undefined && (
        <Alert color={verify.ok ? 'blue' : 'red'} mb="md">
          {verify.ok ? (
            <Text fz="sm">
              Chain intact across{' '}
              <span className="pf-num">{verify.checked}</span> entries.
            </Text>
          ) : (
            <Text fz="sm">
              Chain broken at entry {verify.brokenAtSeq}: {verify.reason}
            </Text>
          )}
        </Alert>
      )}

      {isLoading && <TableSkeleton rows={8} cols={5} />}

      {!isLoading && entries.length === 0 && <EmptyState title="No audit entries" />}

      {!isLoading && entries.length > 0 && (
        <Table.ScrollContainer minWidth={860}>
          <Table className="pf-table" layout="fixed">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w="8%">#</Table.Th>
                <Table.Th w="24%">Action</Table.Th>
                <Table.Th w="18%">Actor</Table.Th>
                <Table.Th w="20%">Target</Table.Th>
                <Table.Th w="14%">Hash</Table.Th>
                <Table.Th w="16%">When</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.map((entry) => (
                <Table.Tr key={entry._id}>
                  <Table.Td className="pf-num">
                    <Text fz="sm" c="dimmed">
                      {entry.seq}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" fw={560}>
                      {entry.action}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" c="gray.7">
                      {entry.actorRoleKey?.replace('_', ' ') ?? 'system'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {entry.target === null ? (
                      <Text fz="sm" c="dimmed">
                        —
                      </Text>
                    ) : (
                      <Group gap={6} wrap="nowrap">
                        <Badge size="xs" variant="light" color="gray">
                          {entry.target.kind}
                        </Badge>
                        <Text className="pf-mono" truncate>
                          {entry.target.id}
                        </Text>
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono" truncate>
                      {entry.hash.slice(0, 12)}…
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text className="pf-mono">{new Date(entry.at).toLocaleString()}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}
